import type { ClientConfig } from '@/api/generated/types'
import { pushSearch } from '@/utils/account/accountPush'
import { StorageKeys, storageService } from '@/utils/localStorageService'

/**
 * The workspace's own records — a COPY in this browser of what the server holds for the signed-in
 * person (`GET /api/me/workspace`). Every write here is pushed there as well (utils/account/
 * accountPush.ts), and signing in fills this copy from the server (accountSync.ts). The copy stays
 * because the stores read it synchronously in their constructors. The CRM asks the server for the
 * people of these runs (POST /api/crm/contacts), with the run ids and tokens recorded here.
 */
export type SearchRecord = {
  id: string
  /** What the buyer calls this search. Absent until they rename it; the list falls back to "Search N". */
  name?: string
  runId: number | null
  /**
   * Подписанный доступ к своему прогону (SearchResult.token). Без него сервер отдаёт строки как чужому:
   * почта и фото срезаны у ВСЕХ строк, включая оплаченные. Может отсутствовать, если человек платил с
   * другой машины — тогда контактов в списке не будет, как и до появления токена.
   */
  token?: string | null
  /** A widened campaign runs a second search; both belong to the same purchased list. */
  growRunIds: number[]
  at: string
  qty: number | null
  total: number | null
  currency: string
  /** Вместимость прогона за год: по ней и только по ней решается, хватит ли людей на пакет. */
  poolY: number | null
  /**
   * Банк — число «сколько подходят», которое печатают все экраны (audienceSegments.bankOf, 2026-10-05).
   * У записей до этого дня его нет: читатели берут `poolY`, как и раньше.
   */
  bank?: number | null
  host: string | null
  rows: number | null
  spec: Partial<ClientConfig>
  /**
   * Откуда запись взялась. `free` — воронка 3 «погружение»: человек вошёл в продукт ДО оплаты, и
   * запись существует, чтобы кабинет вообще открылся (он пускает по наличию записи, Home.tsx).
   * Оплата не меняет `source`, она заполняет `qty`/`total`/`paidAt` — «оплачен» читается по ним
   * (`isPaidSearch`), потому что по ним же считаются кредиты и счёт.
   *
   * `plan` — поиск, запущенный В КАБИНЕТЕ на платном плане (окно New search, 2026-09-23). Денег за
   * него отдельно не платили — его покрывает тариф, и люди списываются из баланса по доставке, как у
   * любого открытого поиска (HomeStore.loadRows). Поэтому `qty`/`total` пустые, в счёт он не попадает,
   * а читается целиком — см. `isReadableSearch`.
   */
  source: 'purchase' | 'backfill' | 'free' | 'plan'
  /**
   * ПОИСК ЕЩЁ ИДЁТ. Дима, 2026-09-29: «сделаем чтобы search… в фоне мог закончиться. Чтобы юзер не
   * уходил». Запись появляется в кабинете, как только сервер вернул номер прогона (LaunchStore onRun),
   * а не через две-три минуты оценки, — иначе закрытое окно New search теряло прогон (заметка от
   * 2026-09-24). Пока флаг стоит, лист не открывается (правило того же дня: лист — после полной
   * оценки), в CRM люди поиска не попадают, а кабинет доводит прогон в фоне (Home/run/RunHost) — и
   * после перезагрузки тоже. Снимается той же recordSearch, что пишет законченный поиск.
   */
  pending?: boolean
  /**
   * Тип воронки В МОМЕНТ ВХОДА (utils/funnel.ts). Пишется здесь, потому что cookie воронки живёт 90
   * дней и без HttpOnly: воронка 3 — единственная, которая просит человека вернуться ПОЗЖЕ, и к
   * моменту оплаты cookie может быть стёрта, заменена или открыта в другом браузере. Тогда события
   * кабинета приехали бы помеченными воронкой 1, и выручка воронки 3 легла бы в чужую колонку.
   */
  funnel?: number
  /** Когда за поиск заплатили. `at` — дата ПРОГОНА, и в воронке 3 она на дни раньше покупки. */
  paidAt?: string | null
  /**
   * Покупка сделана «понарошку» на этой машине (utils/devUnlock.ts), номер её локального заказа. Только
   * по этой пометке откат снимает покупку — настоящую он не тронет никогда.
   */
  localOrder?: string
  /** Запись РОДИЛАСЬ из локальной покупки (чекаут понарошку в воронках 1–2): откат удаляет её целиком. */
  localNew?: true
  /** Номер заказа шлюза: по нему сервер не записывает одну покупку дважды. Нет у старых записей. */
  orderId?: string | null
  /**
   * В чём куплен `qty`: пакет ЛИДОВ (все записи до 2026-09-23 — поля нет) или пакет ОТВЕТОВ (тариф в
   * ответах, utils/pricing.ts). Лиды в режиме ответов пересчитываются по курсу (repliesFromLeads).
   */
  unit?: 'leads' | 'replies'
}

/** Заплачено ли за поиск. Один признак на весь кабинет: по нему же считаются кредиты и счёт. */
export const isPaidSearch = (record: SearchRecord): boolean =>
  record.qty != null || record.total != null

/**
 * Открыт ли лист поиска целиком. Шире, чем «заплачено»: поиск по тарифу (`source: 'plan'`) денег в
 * счёт не приносит, но куплен — тарифом. Счёт и кредиты по-прежнему читают только `isPaidSearch`.
 */
/**
 * ЗАКАЗ БЕЗ ПОИСКА — тариф, купленный на Billing до первого поиска (с 2026-09-30, Дима: «кнопки
 * тарифов должны работать»). Деньги есть, прогона нет: это строка счёта, а не поиск. Кредиты и счёт
 * её видят (`isPaidSearch`), список поисков и их счёт — нет: там она была бы мёртвой карточкой «no run».
 */
export const isOrderOnly = (record: SearchRecord): boolean => record.runId == null && isPaidSearch(record)

/** Записи, которые кабинет показывает и считает поисками: всё, кроме заказов без поиска. */
export const searchesOnly = (records: SearchRecord[]): SearchRecord[] => records.filter((record) => !isOrderOnly(record))

export const isReadableSearch = (record: SearchRecord): boolean =>
  isPaidSearch(record) || record.source === 'plan'

const readSearches = (): SearchRecord[] => {
  const stored = storageService.getItem<SearchRecord[]>(StorageKeys.searches)
  return Array.isArray(stored) ? stored.filter(Boolean) : []
}

/** Newest first: the workspace opens on what was just bought. */
/** Запись → то, что принимает сервер. Ключ прогона едет с ней: для ничейного прогона это доказательство владения. */
export const toSearchInput = (record: SearchRecord) => ({
  id: record.id,
  ...(record.name ? { name: record.name } : {}),
  runId: record.runId,
  token: record.token ?? null,
  growRunIds: record.growRunIds ?? [],
  at: record.at,
  qty: record.qty,
  total: record.total,
  currency: record.currency,
  poolY: record.poolY,
  host: record.host,
  rows: record.rows,
  spec: (record.spec ?? {}) as Record<string, unknown>,
  source: record.source,
  orderId: record.orderId ?? null,
  funnel: record.funnel ?? null,
  paidAt: record.paidAt ?? null,
})

/**
 * На сервер — всё, КРОМЕ двух видов записей, которые браузер может забыть, а сервер — нет (удаления
 * там нет, и забытая запись вернулась бы при следующем входе):
 *   · покупки понарошку (utils/devUnlock.ts): они живут только на этой машине и снимаются «Вернуть
 *     бесплатное»;
 *   · ранней записи фонового поиска (`pending`, Home/run/RunHost): пустой прогон её забывает, и
 *     карточка «найдено 0» вернулась бы с сервера. Уходит, когда прогон дошёл (`pending: false`).
 * Одно правило для отправки записи, для досылки при входе и для первого импорта (account/accountSync).
 */
export const syncsToServer = (record: SearchRecord): boolean => !record.localOrder && !record.localNew && !record.pending

export const pushRecord = (record: SearchRecord) => {
  if (!syncsToServer(record)) return
  pushSearch(toSearchInput(record))
}

export const loadSearches = (): SearchRecord[] =>
  readSearches().sort((a, b) => String(b.at ?? '').localeCompare(String(a.at ?? '')))

export const saveSearches = (list: SearchRecord[]) =>
  storageService.setItem(StorageKeys.searches, list)

const newId = () => `s_${Math.random().toString(16).slice(2, 10)}`

/**
 * Rename a search. Kept next to the record it edits rather than in a store, because the workspace is
 * not the only surface that reads these — the CRM lists them by name too.
 */
export const renameSearch = (id: string, name: string): SearchRecord[] => {
  const list = readSearches().map((record) =>
    record.id === id ? { ...record, name: name.trim() || undefined } : record,
  )
  saveSearches(list)
  const renamed = list.find((record) => record.id === id)
  if (renamed) pushRecord(renamed)
  return list
}

/**
 * The name to show. A buyer's own name wins; otherwise they are numbered in the order they were
 * bought, which is how people refer to them out loud ("the first search").
 */
export const searchName = (record: SearchRecord, all: SearchRecord[], fallback: (n: number) => string) => {
  if (record.name) return record.name
  const oldestFirst = [...all].sort((a, b) => String(a.at).localeCompare(String(b.at)))
  return fallback(oldestFirst.findIndex((entry) => entry.id === record.id) + 1)
}

/**
 * Written at the one moment a search becomes a purchase — the only moment the run, the spec and the
 * receipt are all in hand. Write-once fields (`at`, `qty`, `total`, `host`) keep an old card honest;
 * only `growRunIds` and `spec` are ever updated, so paying twice for the same run does not rewrite
 * history.
 */
/** Число «сколько подходят» для экранов: банк, а у записей до 2026-10-05 — год (см. SearchRecord.bank). */
export const recordBank = (record: Pick<SearchRecord, 'bank' | 'poolY'> | null | undefined): number | null =>
  record ? (record.bank ?? record.poolY ?? null) : null

export const recordSearch = (input: {
  runId: number | null
  spec: Partial<ClientConfig>
  host: string | null
  qty: number | null
  total: number | null
  currency?: string
  poolY: number | null
  /** Банк для экранов (см. SearchRecord.bank). Не передан — не трогать. */
  bank?: number | null
  rows: number | null
  token?: string | null
  at?: string
  source?: SearchRecord['source']
  funnel?: number
  orderId?: string | null
  unit?: SearchRecord['unit']
  /** true — поиск начат и ещё не оценён; false — снять пометку. Не передано — не трогать. */
  pending?: boolean
}, options: { push?: boolean } = {}): SearchRecord => {
  const list = readSearches()
  const existing = input.runId != null ? list.find((record) => record.runId === input.runId) : null

  const record: SearchRecord = existing ?? {
    id: newId(),
    runId: input.runId,
    token: input.token ?? null,
    growRunIds: [],
    at: input.at ?? new Date().toISOString(),
    qty: null,
    total: null,
    currency: 'USD',
    poolY: null,
    host: null,
    rows: null,
    spec: {},
    source: input.source ?? 'purchase',
    ...(input.funnel ? { funnel: input.funnel } : {}),
    paidAt: null,
  }

  /* НАСТОЯЩАЯ ПОКУПКА ПОВЕРХ ЛОКАЛЬНОЙ (utils/devUnlock.ts) — уже не локальная. Без этого метка
     осталась бы на записи, и «Вернуть бесплатное» стёрло бы настоящие деньги (ревью, 2026-09-23). Дата
     оплаты считается от этой покупки. Локальная покупка сама ставит метку заново сразу после записи. */
  if (record.localOrder && (input.qty != null || input.total != null)) {
    delete record.localOrder
    delete record.localNew
    record.paidAt = null
  }

  /* Дата ПОКУПКИ ставится тогда, когда впервые появились деньги, и больше не меняется. В воронке 3
     `at` — дата бесплатного прогона, она на дни раньше; печатать её в счёте значило бы датировать
     покупку прогоном и сортировать счёт не по покупкам. */
  if (!record.paidAt && (input.qty != null || input.total != null)) {
    record.paidAt = input.at ?? new Date().toISOString()
  }
  record.qty = input.qty ?? record.qty
  record.total = input.total ?? record.total
  if (input.unit && input.qty != null) record.unit = input.unit
  record.currency = input.currency ?? record.currency ?? 'USD'
  record.poolY = input.poolY ?? record.poolY
  if (input.bank != null) record.bank = input.bank
  record.host = input.host ?? record.host
  record.rows = input.rows ?? record.rows
  /* Токен не затирается пустым: повторный заход по ссылке без визарда не должен отнимать контакты. */
  record.token = input.token ?? record.token ?? null
  record.spec = input.spec
  record.orderId = record.orderId ?? input.orderId ?? null
  if (input.pending === true) record.pending = true
  else if (input.pending === false) delete record.pending

  if (!existing) list.unshift(record)
  saveSearches(list)
  if (options.push !== false) pushRecord(record)
  return record
}

/**
 * Забыть запись — только в копии браузера. Нужна двум случаям:
 *   · ранней записи поиска (`pending`, Home/run/RunHost): прогон кончился БЕЗ людей — пустая карточка
 *     «найдено 0» была бы поиском, которого нет; или после сбоя нажали «Search again» — новая выборка
 *     заменяет неудавшуюся, как раньше, когда окно писало поиск только в конце;
 *   · дублю покупки, уже записанной на сервере с другого устройства (ThankYou).
 * На сервере удаления нет, поэтому ранняя запись туда и не уходит, пока она `pending` (syncsToServer).
 */
export const forgetSearch = (id: string): SearchRecord[] => {
  const list = readSearches().filter((record) => record.id !== id)
  saveSearches(list)
  return list
}

/**
 * Локальная покупка прямо по записи (devUnlock, «разлочить сейчас»): пакет, сумма 0, дата оплаты и
 * метка. Спека, токен и пул поиска не трогаются — это его собственные.
 */
export const markLocalPurchase = (id: string, qty: number, orderId: string, unit: 'leads' | 'replies' = 'leads'): SearchRecord[] => {
  const at = new Date().toISOString()
  const list = readSearches().map((record) =>
    record.id === id ? { ...record, qty, unit, total: 0, paidAt: record.paidAt ?? at, localOrder: orderId } : record,
  )
  saveSearches(list)
  return list
}

/** Пометить покупку как локальную (devUnlock). Запись уже оплачена recordPurchase — здесь только метка. */
export const tagLocalPurchase = (id: string, orderId: string, created = false): SearchRecord[] => {
  const list = readSearches().map((record) =>
    record.id === id ? { ...record, localOrder: orderId, ...(created ? { localNew: true as const } : {}) } : record,
  )
  saveSearches(list)
  return list
}

/**
 * Снять ВСЕ локальные покупки и вернуть кабинет к тому, каким он был:
 *   - запись, которую РОДИЛА локальная покупка (`localNew`), удаляется целиком;
 *   - поиск из окна New search, открытый ЛОКАЛЬНЫМ тарифом (`source: 'plan'` с меткой), снова `free`;
 *   - у остальных уходят деньги, дата оплаты и метка — сам поиск остаётся.
 * Настоящие покупки без метки не трогаются. Возвращает число снятых записей.
 */
export const clearLocalPurchases = (): number => {
  let cleared = 0
  const list: SearchRecord[] = []
  for (const record of readSearches()) {
    if (!record.localOrder) {
      list.push(record)
      continue
    }
    cleared += 1
    if (record.localNew) continue
    const { localOrder: _order, localNew: _new, unit: _unit, ...rest } = record
    list.push({ ...rest, qty: null, total: null, paidAt: null, source: rest.source === 'plan' ? 'free' : rest.source })
  }
  saveSearches(list)
  return cleared
}

/**
 * A widened campaign runs a SECOND search and both runs belong to the same purchased list, which is
 * why `growRunIds` exists and why the sheet fetches every id in it. Nothing writes them yet —
 * widening an existing campaign is not built — but a record that carries them has to read correctly
 * the day it is.
 */
